import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Form, Spinner, Table } from 'react-bootstrap';
import { useAuth } from '../../context/AuthContext';
import EmptyState from '../../components/EmptyState';
import accountApi from '../../services/accountApi';

function normalizeAuditPayload(data) {
  if (Array.isArray(data)) return { results: data, count: data.length };
  return data || { results: [], count: 0 };
}

function PartnerAuditPage() {
  const { activeTenantId } = useAuth();
  const [events, setEvents] = useState([]);
  const [count, setCount] = useState(0);
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    if (!activeTenantId) return;
    setLoading(true);
    setError(null);
    try {
      const params = {};
      if (action.trim()) params.action = action.trim();
      const data = normalizeAuditPayload(
        await accountApi.listTenantAudit(activeTenantId, params)
      );
      setEvents(data.results || []);
      setCount(data.count ?? (data.results || []).length);
    } catch (err) {
      setError(err.message || 'Failed to load audit events');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTenantId]);

  return (
    <div>
      <h1 className="product-page-title">Partner Activity</h1>
      <p className="product-page-subtitle">
        Bounded security and configuration events for this partner.
      </p>

      {error && <Alert variant="danger">{error}</Alert>}

      <div className="product-card mb-3">
        <Form
          className="row g-2 align-items-end"
          onSubmit={(e) => {
            e.preventDefault();
            load();
          }}
        >
          <div className="col-md-8">
            <Form.Label>Filter by action</Form.Label>
            <Form.Control
              value={action}
              onChange={(e) => setAction(e.target.value)}
              placeholder="e.g. invitation, api_key, member"
            />
          </div>
          <div className="col-md-4">
            <button type="submit" className="btn btn-outline-primary w-100">
              Apply
            </button>
          </div>
        </Form>
      </div>

      {loading && (
        <div className="loading-state">
          <Spinner animation="border" size="sm" className="me-2" />
          Loading…
        </div>
      )}

      {!loading && (
        <div className="product-card">
          <div className="small text-muted mb-2">{count} event{count === 1 ? '' : 's'}</div>
          <Table responsive className="product-table" size="sm">
            <thead>
              <tr>
                <th>Action</th>
                <th>Actor</th>
                <th>Target</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {events.length === 0 ? (
                <tr>
                  <td colSpan={4}>
                    <EmptyState
                      title="No activity yet"
                      description="Team, invitation, and API-key changes will appear here."
                      action={
                        <Link to="/console/team" className="btn btn-sm btn-outline-primary">
                          Open team
                        </Link>
                      }
                    />
                  </td>
                </tr>
              ) : (
                events.map((e) => (
                  <tr key={e.id}>
                    <td className="mono">{e.action}</td>
                    <td>{e.actor_email || e.actor_id || '—'}</td>
                    <td className="mono small">
                      {e.target_type || '—'}
                      {e.target_id ? ` ${String(e.target_id).slice(0, 8)}` : ''}
                    </td>
                    <td>{e.created_at ? new Date(e.created_at).toLocaleString() : '—'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default PartnerAuditPage;
